import React from 'react';
import { observer } from 'mobx-react-lite';

import { ContextualDrawer } from '../../../app/components/contextual-drawer';
import { TagsViewer } from '../../tags/components/tags-viewer';
import { DropdownMenu } from '../../../components/modals/dropdown-menu/DropdownMenu';
import { CollapsibleItem } from '../../../components/collapsible-item/collapsible-item';
import { VersionListItem } from './version-list-item';

import type { Asset } from '../../../app/entities/asset';

import styles from './asset-viewer-context-drawer.module.scss';
interface AssetViewerContextDrawerProps {
  asset: Asset;
  selectedAsset: Asset;
  actions: React.ComponentProps<typeof DropdownMenu>['items'];
  onVersionSelect: (version: Asset) => void;
}

export const AssetViewerContextDrawer: React.FC<AssetViewerContextDrawerProps> = observer(
  ({ asset, selectedAsset, actions, onVersionSelect }) => {
    const uploader = selectedAsset.uploaderName || asset.uploaderName;
    const relativeTime = selectedAsset.relativeUploadTime || asset.relativeUploadTime;

    const actionsHaveIcon = actions.some((item) => !!item.icon);

    return (
      <ContextualDrawer>
        <div className={styles['info-container']}>
          <div className={styles['card']}>
            <h6 className={styles['title']}>
              {uploader}
              {uploader && relativeTime && ` - `}
              {relativeTime}
            </h6>

            <TagsViewer
              key={`context-drawer-tag-${selectedAsset._id}`}
              targetType="asset"
              targetId={asset._id}
            />
          </div>

          {(asset.type === 'version_stack' || selectedAsset?.version! > 0) && (
            <CollapsibleItem
              title={<h6 className={styles['title']}>Versions</h6>}
              triggerType="transparent-small"
              padding="none"
              defaultOpen
              isTransparent
            >
              <div className={styles['collapsible-content']} data-gap="narrow">
                {asset.sortedChildren.map((asset) => (
                  <VersionListItem
                    key={asset._id}
                    onClick={() => onVersionSelect(asset)}
                    thumbnail={asset.cover?.src || asset.thumbnail}
                    isSelected={selectedAsset?._id === asset._id}
                    name={asset.name}
                    version={asset.version || 1}
                    date={asset.createdAt}
                    uploader={asset.uploader?.firstName || ''}
                    fileType={asset.fileType}
                    extension={asset.extension}
                  />
                ))}
              </div>
            </CollapsibleItem>
          )}

          {!!actions?.length && (
            <div>
              <h6 className={styles['title']}>Actions</h6>

              <div className={styles['collapsible-content']}>
                {actions.map((action) => {
                  if (action.hidden) {
                    return;
                  }

                  return (
                    <div
                      className={styles['action-item']}
                      key={action.title as string}
                      data-type={action.type}
                      data-disabled={action.disabled}
                      onClick={
                        action.onSelect as React.MouseEventHandler<HTMLDivElement> | undefined
                      }
                    >
                      {actionsHaveIcon && (
                        <div className={styles['action-item-icon']}>{action.icon}</div>
                      )}

                      {action.title}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </ContextualDrawer>
    );
  },
);
